import { useState, useRef } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
  Tabs,
  Tab,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import ExcelJS from "exceljs";
import { createAndAddMultipleUsersToUsersList } from "../axios/axiosFunctions";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const RELATIONSHIP_LABEL_TO_VALUE = {
  manager: "manager",
  "direct report": "direct",
  direct: "direct",
  peer: "peer",
  "other colleague": "otherColleague",
  othercolleague: "otherColleague",
  other: "otherColleague",
};

const RELATIONSHIP_DISPLAY = {
  manager: "Manager",
  direct: "Direct Report",
  peer: "Peer",
  otherColleague: "Other Colleague",
};

const TEMPLATE_BLANK_ROWS = 15;

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`xlsx-per-reviewee-tabpanel-${index}`}
      aria-labelledby={`xlsx-per-reviewee-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const SIMPLE360_MAX_PROVIDERS = 12;

const XlsxUploadPerReviewee360 = ({
  open,
  onClose,
  groupId,
  revieweeId,
  revieweeFirstName,
  revieweeLastName,
  revieweeEmail,
  onSuccess,
  category,
  currentReviewerCount = 0,
}) => {
  const isSimple360 = category === "simple360";
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [parsedReviewers, setParsedReviewers] = useState([]);
  const [errors, setErrors] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [alertMsg, setAlertMsg] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("error");
  const [showAlert, setShowAlert] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [invalidRows, setInvalidRows] = useState([]);

  const revieweeFullName = [revieweeFirstName, revieweeLastName]
    .filter(Boolean)
    .join(" ")
    .trim() || revieweeEmail || "Reviewee";

  const downloadTemplate = async () => {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = "K2G Assessments";
    workbook.created = new Date();
    const worksheet = workbook.addWorksheet("360 Reviewers");

    worksheet.columns = [
      { header: "firstName", key: "firstName", width: 20 },
      { header: "lastName", key: "lastName", width: 20 },
      { header: "email", key: "email", width: 32 },
      { header: "revieweeEmail", key: "revieweeEmail", width: 32 },
      { header: "relationshipType", key: "relationshipType", width: 22 },
    ];

    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(1).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF0F5385" },
    };
    worksheet.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };

    for (let i = 0; i < TEMPLATE_BLANK_ROWS; i++) {
      worksheet.addRow({ revieweeEmail });
    }

    const lastRow = TEMPLATE_BLANK_ROWS + 1;
    for (let r = 2; r <= lastRow; r++) {
      worksheet.getCell(`E${r}`).dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [
          isSimple360
            ? '"Manager,Direct Report,Peer"'
            : '"Manager,Direct Report,Peer,Other Colleague"',
        ],
        showErrorMessage: true,
        errorStyle: "error",
        errorTitle: "Invalid relationship",
        error: isSimple360
          ? "Please select Manager, Direct Report, or Peer"
          : "Please select Manager, Direct Report, Peer, or Other Colleague",
      };
      worksheet.getCell(`D${r}`).font = { color: { argb: "FF888888" } };
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    const safeName = revieweeFullName.replace(/[^a-z0-9]+/gi, "_");
    link.setAttribute("href", url);
    link.setAttribute("download", `360_reviewers_${safeName}.xlsx`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const normalizeRelationship = (value) => {
    if (!value) return null;
    const normalized = String(value).toLowerCase().trim();
    return RELATIONSHIP_LABEL_TO_VALUE[normalized] || null;
  };

  const cellText = (cell) => {
    if (cell == null) return "";
    const v = cell.value;
    if (v == null) return "";
    if (typeof v === "object") {
      if (v.text) return String(v.text).trim();
      if (v.result) return String(v.result).trim();
      if (Array.isArray(v.richText)) {
        return v.richText.map((rt) => rt.text || "").join("").trim();
      }
      return "";
    }
    return String(v).trim();
  };

  const parseXlsx = async (file) => {
    const buffer = await file.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);
    const worksheet = workbook.worksheets[0];
    if (!worksheet) {
      return {
        reviewers: [],
        errors: ["Workbook has no worksheets"],
        invalidRows: [],
      };
    }

    const reviewers = [];
    const parseErrors = [];
    const invalidRows = [];
    const seenReviewerEmails = new Set();
    const expectedRevieweeEmail = (revieweeEmail || "").toLowerCase();

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return;

      const firstName = cellText(row.getCell(1));
      const lastName = cellText(row.getCell(2));
      const email = cellText(row.getCell(3));
      const revieweeEmailValue = cellText(row.getCell(4));
      const relationshipValue = cellText(row.getCell(5));

      if (!firstName && !lastName && !email && !relationshipValue) {
        return;
      }

      const rowData = {
        line: rowNumber,
        data: { firstName, lastName, email, revieweeEmailValue, relationshipValue },
        errors: [],
      };

      if (!firstName) rowData.errors.push("First name is required");
      if (!email) {
        rowData.errors.push("Email is required");
      } else if (!validateEmail(email)) {
        rowData.errors.push("Invalid email format");
      } else {
        const emailLower = email.toLowerCase();
        if (seenReviewerEmails.has(emailLower)) {
          rowData.errors.push(`Duplicate email: ${email}`);
        }
        if (emailLower === expectedRevieweeEmail) {
          rowData.errors.push("Reviewer email cannot match the reviewee's email");
        }
      }

      if (revieweeEmailValue && revieweeEmailValue.toLowerCase() !== expectedRevieweeEmail) {
        rowData.errors.push(
          `revieweeEmail "${revieweeEmailValue}" does not match this reviewee (${revieweeEmail})`,
        );
      }

      // Simple 360 Feedback: blank relationship defaults to "peer"
      let relationshipNormalized = normalizeRelationship(relationshipValue);
      if (isSimple360) {
        relationshipNormalized = relationshipNormalized || "peer";
      } else if (!relationshipValue) {
        rowData.errors.push("Relationship type is required");
      } else if (!relationshipNormalized) {
        rowData.errors.push(
          `Invalid relationship "${relationshipValue}". Use Manager, Direct Report, Peer, or Other Colleague`,
        );
      }

      if (
        isSimple360 &&
        rowData.errors.length === 0 &&
        currentReviewerCount + reviewers.length >= SIMPLE360_MAX_PROVIDERS
      ) {
        rowData.errors.push(
          `A feedback receiver can have at most ${SIMPLE360_MAX_PROVIDERS} feedback providers`,
        );
      }

      if (rowData.errors.length > 0) {
        invalidRows.push(rowData);
        parseErrors.push(`Row ${rowNumber}: ${rowData.errors.join("; ")}`);
        return;
      }

      seenReviewerEmails.add(email.toLowerCase());
      reviewers.push({
        firstName,
        lastName,
        email,
        type: relationshipNormalized,
      });
    });

    return { reviewers, errors: parseErrors, invalidRows };
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!/\.xlsx?$/i.test(file.name)) {
      setAlertMsg("Please select an .xlsx file");
      setAlertSeverity("error");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 3000);
      return;
    }

    try {
      const { reviewers, errors: parseErrors, invalidRows: invalidRowsData } =
        await parseXlsx(file);

      setParsedReviewers(reviewers);
      setErrors(parseErrors);
      setInvalidRows(invalidRowsData);
      setShowPreview(true);
      setTabValue(0);
    } catch (error) {
      console.error("xlsx parse error:", error);
      setAlertMsg("Error reading .xlsx file");
      setAlertSeverity("error");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 3000);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUpload = async () => {
    if (parsedReviewers.length === 0) {
      setAlertMsg("No valid reviewer data to upload");
      setAlertSeverity("error");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 3000);
      return;
    }

    setLoading(true);
    try {
      const result = await createAndAddMultipleUsersToUsersList(
        groupId,
        revieweeId,
        { users: parsedReviewers },
      );

      if (result?.success === true) {
        const created = result.createdCount ?? 0;
        const existing = result.existingAddedCount ?? 0;
        setLoading(false);
        setAlertMsg(
          `Added ${created + existing} reviewer(s) (${created} new, ${existing} existing)`,
        );
        setAlertSeverity("success");
        setShowAlert(true);
        setTimeout(() => {
          setShowAlert(false);
          handleClose();
          if (onSuccess) onSuccess();
        }, 1800);
      } else {
        setLoading(false);
        setAlertMsg(result?.msg || "Failed to upload reviewers");
        setAlertSeverity("error");
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 5000);
      }
    } catch (error) {
      console.error(error);
      setLoading(false);
      setAlertMsg("Error uploading reviewers");
      setAlertSeverity("error");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 3000);
    }
  };

  const handleClose = () => {
    setParsedReviewers([]);
    setErrors([]);
    setInvalidRows([]);
    setShowPreview(false);
    setShowAlert(false);
    setTabValue(0);
    onClose();
  };

  const hasValidData = parsedReviewers.length > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">
            Add Reviewers via Excel — {revieweeFullName}
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Download the .xlsx template (pre-filled for{" "}
            <strong>{revieweeFullName}</strong>), add reviewers, and upload it
            back. The relationship column is a dropdown.
          </Typography>

          <Box display="flex" gap={2} sx={{ mb: 2 }} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={downloadTemplate}
            >
              Download Template for {revieweeFullName}
            </Button>
            <Button
              variant="contained"
              component="label"
              startIcon={<CloudUploadIcon />}
            >
              Select .xlsx File
              <VisuallyHiddenInput
                type="file"
                accept=".xlsx"
                onChange={handleFileSelect}
                ref={fileInputRef}
              />
            </Button>
          </Box>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            <strong>Columns:</strong> firstName, lastName, email,
            revieweeEmail (pre-filled), relationshipType (dropdown)
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {errors.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Validation Warnings:</Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {errors.slice(0, 10).map((err, i) => (
                <li key={i}>{err}</li>
              ))}
              {errors.length > 10 && <li>...and {errors.length - 10} more</li>}
            </ul>
          </Alert>
        )}

        {showPreview && (hasValidData || invalidRows.length > 0) && (
          <>
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
              <Tabs
                value={tabValue}
                onChange={(e, newValue) => setTabValue(newValue)}
                aria-label="preview tabs"
              >
                <Tab label={`Reviewers (${parsedReviewers.length})`} id="tab-0" />
                {invalidRows.length > 0 && (
                  <Tab
                    label={`Invalid Rows (${invalidRows.length})`}
                    id="tab-1"
                    sx={{ color: "error.main" }}
                  />
                )}
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              {parsedReviewers.length > 0 ? (
                <TableContainer component={Paper} sx={{ maxHeight: 360 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>First Name</strong></TableCell>
                        <TableCell><strong>Last Name</strong></TableCell>
                        <TableCell><strong>Email</strong></TableCell>
                        <TableCell><strong>Relationship</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parsedReviewers.map((u, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{u.firstName}</TableCell>
                          <TableCell>{u.lastName}</TableCell>
                          <TableCell>{u.email}</TableCell>
                          <TableCell>
                            {RELATIONSHIP_DISPLAY[u.type] || u.type}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="textSecondary">
                  No valid reviewers to display
                </Typography>
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              {invalidRows.length > 0 && (
                <Box>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">
                      {invalidRows.length} row(s) with errors:
                    </Typography>
                  </Alert>
                  <TableContainer component={Paper} sx={{ maxHeight: 360 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#ffebee" }}>
                          <TableCell><strong>Row</strong></TableCell>
                          <TableCell><strong>Email</strong></TableCell>
                          <TableCell><strong>Error(s)</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invalidRows.map((row, idx) => (
                          <TableRow
                            key={idx}
                            sx={{ backgroundColor: "#fff3e0" }}
                          >
                            <TableCell>{row.line}</TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {row.data.email || "—"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box>
                                {row.errors.map((error, errIdx) => (
                                  <Typography
                                    key={errIdx}
                                    variant="caption"
                                    display="block"
                                    color="error"
                                    sx={{ mb: 0.5 }}
                                  >
                                    • {error}
                                  </Typography>
                                ))}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </TabPanel>
          </>
        )}

        {showAlert && (
          <Alert severity={alertSeverity} sx={{ mt: 2 }}>
            {alertMsg}
          </Alert>
        )}

        <Box display="flex" justifyContent="center" gap={2} sx={{ mt: 3 }}>
          {hasValidData && (
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? "Uploading..." : `Add ${parsedReviewers.length} Reviewer(s)`}
            </Button>
          )}
          <Button variant="outlined" onClick={handleClose} color="inherit">
            Cancel
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default XlsxUploadPerReviewee360;
